import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Button, Input, Space, Tag, Drawer, Form, Row, Col,
    Select, message, Typography, Popconfirm, Switch, Tooltip, Empty
} from 'antd';
import {
    PlusOutlined, EditOutlined, ReloadOutlined, SearchOutlined,
    CheckCircleOutlined, StopOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const api = (path, method = 'GET', data = null) => {
    const token = localStorage.getItem('token');
    return axios({ method, url: `/api${path}`, data, headers: { Authorization: `Bearer ${token}` } });
};

// ── Suppliers Page ────────────────────────────────────────────────────────────

const Suppliers = () => {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const fetchSuppliers = useCallback(async () => {
        setLoading(true);
        try {
            const params = search ? `?search=${encodeURIComponent(search)}` : '';
            const res = await api(`/suppliers${params}`);
            setSuppliers(res.data);
        } catch {
            message.error('Failed to load suppliers');
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

    const openCreate = () => {
        setEditingSupplier(null);
        form.resetFields();
        setDrawerOpen(true);
    };

    const openEdit = (supplier) => {
        setEditingSupplier(supplier);
        form.setFieldsValue({
            name: supplier.name,
            email: supplier.email,
            phone: supplier.phone,
            mobile: supplier.mobile,
            address: supplier.address,
            city: supplier.city,
            zip: supplier.zip,
            notes: supplier.notes,
        });
        setDrawerOpen(true);
    };

    const handleSave = async (values) => {
        setSaving(true);
        try {
            if (editingSupplier) {
                await api(`/suppliers/${editingSupplier.id}`, 'PUT', values);
                message.success('Supplier updated');
            } else {
                await api('/suppliers', 'POST', values);
                message.success('Supplier created');
            }
            setDrawerOpen(false);
            fetchSuppliers();
        } catch (e) {
            message.error(e.response?.data?.details || e.response?.data?.error || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (supplier) => {
        try {
            const newActive = supplier.status !== 'active';
            await api(`/suppliers/${supplier.id}/status`, 'PATCH', { active: newActive });
            message.success(`Supplier ${newActive ? 'activated' : 'deactivated'}`);
            fetchSuppliers();
        } catch {
            message.error('Failed to update supplier status');
        }
    };

    const columns = [
        {
            title: 'Name', dataIndex: 'name', key: 'name',
            render: (name, r) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{name}</Text>
                    {r.code && <Text type="secondary" style={{ fontSize: 11 }}>{r.code}</Text>}
                </Space>
            )
        },
        { title: 'Email', dataIndex: 'email', key: 'email', render: v => v || '—' },
        { title: 'Phone', dataIndex: 'phone', key: 'phone', render: v => v || '—' },
        { title: 'City', dataIndex: 'city', key: 'city', render: v => v || '—' },
        { title: 'Country', dataIndex: 'country', key: 'country', render: v => v || '—' },
        {
            title: 'Status', dataIndex: 'status', key: 'status',
            render: (s) => s === 'active'
                ? <Tag icon={<CheckCircleOutlined />} color="success">Active</Tag>
                : <Tag icon={<StopOutlined />} color="default">Inactive</Tag>
        },
        {
            title: 'Actions', key: 'actions', align: 'center',
            render: (_, record) => (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>Edit</Button>
                    <Tooltip title={record.status === 'active' ? 'Deactivate' : 'Activate'}>
                        <Popconfirm
                            title={`${record.status === 'active' ? 'Deactivate' : 'Activate'} supplier "${record.name}"?`}
                            onConfirm={() => handleToggleStatus(record)}
                            okText="Yes" cancelText="No"
                        >
                            <Button
                                size="small"
                                danger={record.status === 'active'}
                                type={record.status !== 'active' ? 'primary' : 'default'}
                            >
                                {record.status === 'active' ? 'Deactivate' : 'Activate'}
                            </Button>
                        </Popconfirm>
                    </Tooltip>
                </Space>
            )
        },
    ];

    return (
        <Card>
            {/* Header */}
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col><Title level={4} style={{ margin: 0 }}>Suppliers</Title></Col>
                <Col>
                    <Space>
                        <Input
                            placeholder="Search by name, code, phone…"
                            prefix={<SearchOutlined />}
                            style={{ width: 260 }}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onPressEnter={fetchSuppliers}
                            allowClear
                            onClear={() => setSearch('')}
                        />
                        <Button icon={<ReloadOutlined />} onClick={fetchSuppliers} />
                        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                            Add Supplier
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={suppliers}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 15 }}
                size="middle"
                locale={{
                    emptyText: (
                        <Empty
                            description={search ? `No suppliers match "${search}"` : 'No suppliers yet. Click "Add Supplier" to create one.'}
                        />
                    )
                }}
            />

            {/* Create / Edit Drawer */}
            <Drawer
                title={editingSupplier ? `Edit — ${editingSupplier.name}` : 'Add Supplier'}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                width={500}
                footer={null}
            >
                <Form form={form} layout="vertical" onFinish={handleSave}>
                    <Form.Item name="name" label="Supplier Name" rules={[{ required: true, message: 'Name is required' }]}>
                        <Input placeholder="e.g. ABC Trading Co." />
                    </Form.Item>

                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="email" label="Email"
                                rules={[{ type: 'email', message: 'Please enter a valid email address' }]}>
                                <Input type="email" placeholder="contact@supplier.com" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="phone" label="Phone">
                                <Input placeholder="079-xxx-xxxx" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="mobile" label="Mobile">
                        <Input placeholder="078-xxx-xxxx" />
                    </Form.Item>

                    <Form.Item name="address" label="Address">
                        <Input placeholder="Street address" />
                    </Form.Item>

                    <Row gutter={12}>
                        <Col span={14}>
                            <Form.Item name="city" label="City">
                                <Input placeholder="Amman" />
                            </Form.Item>
                        </Col>
                        <Col span={10}>
                            <Form.Item name="zip" label="ZIP">
                                <Input placeholder="11118" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={3} placeholder="Any internal notes about this supplier…" />
                    </Form.Item>

                    <Row justify="end" style={{ marginTop: 8 }}>
                        <Space>
                            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={saving}>
                                {editingSupplier ? 'Save Changes' : 'Create Supplier'}
                            </Button>
                        </Space>
                    </Row>
                </Form>
            </Drawer>
        </Card>
    );
};

export default Suppliers;
